import { motion, useInView } from 'framer-motion';
import { useRef, ReactNode } from 'react';

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  y?: number;
  blur?: boolean;
}

// Premium slow, confident reveal animation
export function ScrollReveal({ 
  children, 
  className = '', 
  delay = 0, 
  duration = 0.9,
  y = 24,
  blur = false
}: ScrollRevealProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <motion.div
      ref={ref}
      initial={{ 
        opacity: prefersReducedMotion ? 1 : 0, 
        y: prefersReducedMotion ? 0 : y,
        filter: blur && !prefersReducedMotion ? 'blur(8px)' : 'blur(0px)'
      }}
      animate={isInView || prefersReducedMotion ? { 
        opacity: 1, 
        y: 0,
        filter: 'blur(0px)'
      } : { 
        opacity: 0, 
        y,
        filter: blur ? 'blur(8px)' : 'blur(0px)'
      }}
      transition={prefersReducedMotion ? { 
        duration: 0.01
      } : {
        duration, 
        delay, 
        ease: [0.25, 0.1, 0.25, 1] // Premium cubic-bezier for slow, confident motion
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

interface StaggerContainerProps {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
}

export function StaggerContainer({ 
  children, 
  className = '',
  staggerDelay = 0.15 
}: StaggerContainerProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: staggerDelay,
            delayChildren: 0.1,
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ 
  children, 
  className = '' 
}: { 
  children: ReactNode; 
  className?: string;
}) {
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <motion.div
      variants={{
        hidden: { opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : 20 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: prefersReducedMotion ? {
            duration: 0.01
          } : {
            duration: 0.8,
            ease: [0.25, 0.1, 0.25, 1]
          }
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Sequential number reveal for metrics
export function SequentialReveal({ 
  children, 
  index = 0,
  className = '' 
}: { 
  children: ReactNode; 
  index?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      transition={{ 
        duration: 1,
        delay: index * 0.2, 
        ease: [0.25, 0.1, 0.25, 1]
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
